// src/pages/PaymentSuccess.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Crown, CheckCircle, Sparkles, ArrowRight, Star, Trophy, Zap } from 'lucide-react';
import { verifyPaymentSession } from '../config/stripe';
import { COURSE_PACKAGES } from '../config/coursePackages';

function PaymentSuccess() {
  const { user, authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session_id');
  const isRenewal = searchParams.get('renewal') === 'true';  // ✅ NEW: Check for renewal

  useEffect(() => {
    const verifyPayment = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      // ✅ NEW: Handle saved payment method renewals (no session_id needed)
      if (isRenewal && !sessionId) {
        console.log('✅ Saved payment method renewal - no verification needed');
        try {
          // Just get the updated user profile
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
          setVerificationStatus('success');
        } catch (error) {
          console.error('Error loading user profile:', error);
          setVerificationStatus('error');
        } finally {
          setIsVerifying(false);
        }
        return;
      }

      // ✅ EXISTING: Handle regular checkout sessions (needs verification)
      if (!sessionId) {
        console.error('No session ID provided');
        setVerificationStatus('error');
        setIsVerifying(false);
        return;
      }

      try {
        // Verify the payment session with Cloud Functions
        const result = await verifyPaymentSession(sessionId, user.uid);
        
        if (result.success) {
          // Get updated user profile (subscription should already be updated by Cloud Function)
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
          
          setVerificationStatus('success');
        } else {
          setVerificationStatus('error');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setVerificationStatus('error');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [user, authLoading, sessionId, isRenewal, navigate]);  // ✅ Added isRenewal to dependencies

  const handleContinue = () => {
    // Add a small delay for dramatic effect
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 500);
  };

  // Loading state
  if (authLoading || isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Crown className="text-purple-500 animate-bounce" size={32} />
            <Sparkles className="text-yellow-400 animate-pulse" size={24} />
          </div>
          <div className="w-12 h-12 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-700 text-lg font-semibold">
            {isRenewal ? 'Processing your renewal...' : 'Verifying your payment...'}
          </div>
          <div className="text-gray-500 text-sm mt-2">This will just take a moment</div>
        </div>
      </div>
    );
  }

  // Success state
  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-lg w-full relative overflow-hidden">
          {/* Animated background sparkles */}
          <div className="absolute inset-0 pointer-events-none">
            <Sparkles className="absolute top-6 left-6 text-yellow-300 animate-pulse" size={16} />
            <Star className="absolute top-12 right-8 text-purple-300 animate-bounce" size={12} />
            <Sparkles className="absolute bottom-8 left-12 text-blue-300 animate-pulse" size={14} />
            <Star className="absolute bottom-6 right-6 text-green-300 animate-bounce" size={10} />
          </div>
          
          <div className="relative z-10">
            {/* Success icon with animation */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <CheckCircle className="text-green-500 animate-pulse" size={64} />
                <div className="absolute -top-2 -right-2">
                  <Crown className="text-yellow-400 animate-bounce" size={24} />
                </div>
              </div>
            </div>

            {/* Success message */}
            <div className="text-green-600 text-3xl font-black mb-2">
              {isRenewal ? 'Renewal Successful!' : 'Payment Successful!'}
            </div>
            <div className="text-gray-600 text-lg mb-6">
              {isRenewal 
                ? 'Your course has been renewed successfully!' 
                : 'Welcome to The Olive Tree Learn!'
              }
            </div>

            {/* Course info if available */}
            {userProfile?.course && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 justify-center mb-2">
                  <Trophy className="text-purple-600" size={20} />
                  <span className="font-bold text-purple-800">Course Details</span>
                </div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div><strong>Package:</strong> {COURSE_PACKAGES[userProfile.course.packageId]?.name || 'Course'}</div>
                  <div><strong>Status:</strong> <span className="text-green-600 font-medium">Active</span></div>
                  {userProfile.course.endDate && (
                    <div><strong>Access until:</strong> {new Date(userProfile.course.endDate.seconds * 1000).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            )}

            {/* Features preview */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="text-gray-800 font-semibold mb-3">What's included:</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Zap className="text-yellow-500" size={16} />
                  <span>Interactive Games</span>
                </div>
                <div className="flex items-center gap-2">
                  <Crown className="text-purple-500" size={16} />
                  <span>Live Classes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="text-blue-500" size={16} />
                  <span>AI Tutor</span>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy className="text-green-500" size={16} />
                  <span>Certificate</span>
                </div>
              </div>
            </div>

            {/* Continue button */}
            <button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
            >
              <span>Start Learning</span>
              <ArrowRight size={20} />
            </button>

            <div className="text-gray-500 text-xs mt-4">
              🎉 Thank you for choosing The Olive Tree Learn!
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - ✅ IMPROVED: Better error message for renewals
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-pink-900 to-purple-900 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <div className="text-red-600 text-2xl font-bold mb-4">
          {isRenewal ? 'Renewal Verification Failed' : 'Payment Verification Failed'}
        </div>
        <div className="text-gray-700 mb-6">
          {isRenewal 
            ? 'We couldn\'t load your renewal details. Please check your account or contact support.'
            : `We couldn't verify your payment. Please contact support with your session ID: ${sessionId || 'N/A'}`
          }
        </div>
        <button 
          onClick={() => navigate(isRenewal ? '/account' : '/complete-subscription')}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 mr-3"
        >
          {isRenewal ? 'Check Account' : 'Try Again'}
        </button>
        <button 
          onClick={() => window.location.href = 'mailto:support@yourdomain.com'}
          className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
        >
          Contact Support
        </button>
      </div>
    </div>
  );
}

export default PaymentSuccess;